// @greenlight-spec: 19-design-system.md
import * as React from 'react';

interface Options {
  onDismiss: () => void;
  /**
   * How far (px) past the collapsed detent the user must drag down before the
   * sheet dismisses. Defaults to 80px — a comfortable intentional flick.
   */
  threshold?: number;
  /** Pass false to disable (e.g. when not on a phone). */
  enabled?: boolean;
  /**
   * When this value changes the hook resets all drag state and clears any
   * inline height/transform on the panel. Pass the currently-open item's id
   * so that switching entries (while the sheet stays open) doesn't carry over
   * the previous entry's expanded height or wrong collapsed-detent measurement.
   */
  resetKey?: unknown;
}

export interface SheetDragHandlers {
  handleRef: React.RefObject<HTMLDivElement>;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

/** Duration of the slide-off animation once a dismiss is committed. */
const DISMISS_MS = 200;
/** Fraction of the viewport the fully-expanded ("full") detent occupies. */
const FULL_FRACTION = 0.92;
const SNAP = 'height 0.25s cubic-bezier(0.2,0.8,0.2,1)';

/** True when the user has opted into reduced motion. Defaults to reduced when
 *  matchMedia is unavailable (SSR / test environments without a stub). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Attaches drag-to-resize + drag-to-dismiss behaviour to a bottom sheet's grab
 * handle, giving the sheet two detents (iOS-style):
 *
 *   - collapsed — the sheet's natural content height (its open height)
 *   - full      — 92% of the viewport
 *
 * Drag the handle UP to grow the sheet toward full; drag DOWN to shrink it back
 * toward collapsed, and past the collapsed detent (by `threshold`) to dismiss.
 * On release the sheet snaps to whichever detent is nearer, or slides off.
 *
 * Resizing is driven by the panel's inline `height` so the gesture has live
 * feedback and the inner scroll area (flex-1 / overflow-y-auto) reflows
 * naturally. Pointer events are used rather than touch events so the gesture
 * works with a mouse (a desktop browser at phone width) as well as real touch,
 * and pointer capture keeps move/up flowing even when the pointer leaves the
 * handle.
 *
 * Pass `resetKey` (the currently-open entry's id) so that switching entries
 * while the sheet stays open resets the panel to its natural height. Without
 * it the reused Radix Content node keeps the previous entry's expanded height.
 */
export function useSheetDrag({
  onDismiss,
  threshold = 80,
  enabled = true,
  resetKey,
}: Options): SheetDragHandlers {
  const handleRef = React.useRef<HTMLDivElement>(null);
  // Keep a stable ref so the dismiss timer always sees the latest onDismiss.
  const onDismissRef = React.useRef(onDismiss);
  React.useLayoutEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const drag = React.useRef({ active: false, startY: 0, startH: 0 });
  const collapsedH = React.useRef(0);
  const expanded = React.useRef(false);
  const liveH = React.useRef(0);
  const dismissTimer = React.useRef<number | null>(null);

  const panel = (): HTMLElement | null =>
    (handleRef.current?.parentElement as HTMLElement | null) ?? null;

  const fullHeight = (): number => Math.round(window.innerHeight * FULL_FRACTION);

  // Cancel a pending dismiss timer if the sheet is closed another way (or the
  // component unmounts) before the slide-off animation completes — otherwise
  // the callback could fire against a detached node.
  React.useEffect(() => {
    return () => {
      if (dismissTimer.current != null) {
        window.clearTimeout(dismissTimer.current);
        dismissTimer.current = null;
      }
    };
  }, []);

  // When the hook is disabled (e.g. viewport crosses 640px), clear ALL inline
  // drag styles from the panel — not just when a drag is actively in progress.
  // Without this a sheet sitting at full height while the viewport is widened
  // keeps its phone-sized inline height on the right-side sheet.
  React.useEffect(() => {
    if (enabled) return;
    drag.current = { active: false, startY: 0, startH: 0 };
    const p = panel();
    if (p && (p.style.height || p.style.transform)) {
      p.style.transition = '';
      p.style.height = '';
      p.style.transform = '';
    }
  }, [enabled]);

  // Reset all drag state when the open item changes (resetKey). The two
  // knowledge sheets stay open while switching between entries, so the Radix
  // panel's DOM node is reused. Clearing the inline height lets the new entry
  // render at its natural height and re-measures the collapsed detent correctly.
  React.useEffect(() => {
    drag.current = { active: false, startY: 0, startH: 0 };
    collapsedH.current = 0;
    expanded.current = false;
    liveH.current = 0;
    const p = panel();
    if (p) {
      p.style.transition = '';
      p.style.height = '';
      p.style.transform = '';
    }
  }, [resetKey]);

  const clearTransitionAfter = React.useCallback((p: HTMLElement) => {
    const done = (): void => {
      p.style.transition = '';
      p.removeEventListener('transitionend', done);
    };
    p.addEventListener('transitionend', done, { once: true });
  }, []);

  const snapTo = React.useCallback(
    (p: HTMLElement, height: number) => {
      if (prefersReducedMotion()) {
        p.style.transition = '';
        p.style.height = `${height}px`;
      } else {
        p.style.transition = SNAP;
        p.style.height = `${height}px`;
        clearTransitionAfter(p);
      }
    },
    [clearTransitionAfter],
  );

  const reset = React.useCallback(() => {
    drag.current = { active: false, startY: 0, startH: 0 };
  }, []);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      const p = panel();
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      // Cancel any in-flight dismiss animation so a new drag can take over.
      if (dismissTimer.current != null) {
        window.clearTimeout(dismissTimer.current);
        dismissTimer.current = null;
        p.style.transform = '';
      }
      const rect = p.getBoundingClientRect();
      // A fresh (remounted) node has no inline height: re-measure the collapsed
      // detent and treat the sheet as collapsed.
      if (p.style.height === '') {
        collapsedH.current = Math.round(rect.height);
        expanded.current = false;
      }
      p.style.transition = 'none';
      drag.current = { active: true, startY: e.clientY, startH: rect.height };
      liveH.current = rect.height;
    },
    [enabled],
  );

  const onPointerMove = React.useCallback((e: React.PointerEvent) => {
    if (!drag.current.active) return;
    const p = panel();
    if (!p) return;
    const delta = e.clientY - drag.current.startY; // down = positive
    // Drag up (negative delta) grows the sheet; drag down shrinks it.
    const next = Math.min(drag.current.startH - delta, fullHeight());
    liveH.current = Math.max(next, 0);
    p.style.height = `${liveH.current}px`;
  }, []);

  const settle = React.useCallback(() => {
    const p = panel();
    if (!p) return;
    const collapsed = collapsedH.current;
    const full = fullHeight();
    // Dragged down well past the collapsed detent → dismiss.
    if (liveH.current <= collapsed - threshold) {
      const reducedMotion = prefersReducedMotion();
      p.style.transition = reducedMotion ? '' : `transform ${DISMISS_MS}ms ease-out`;
      p.style.transform = 'translateY(100%)';
      // Clear any existing timer before scheduling a new one so a rapid second
      // dismiss gesture can't stack multiple onDismiss callbacks.
      if (dismissTimer.current != null) {
        window.clearTimeout(dismissTimer.current);
      }
      // When reduced motion is active the transform fires instantly; use 0ms so
      // the dismiss callback still flows through the same async path.
      const delay = reducedMotion ? 0 : DISMISS_MS;
      dismissTimer.current = window.setTimeout(() => {
        dismissTimer.current = null;
        const node = panel();
        // Suppress Radix's exit animation so the panel doesn't snap back to its
        // open position for a frame before unmounting.
        if (node) node.style.animation = 'none';
        onDismissRef.current();
      }, delay);
      return;
    }
    // Otherwise snap to whichever detent is nearer.
    const goFull = liveH.current >= (collapsed + full) / 2;
    expanded.current = goFull;
    snapTo(p, goFull ? full : collapsed);
  }, [threshold, snapTo]);

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (!drag.current.active) return;
      reset();
      settle();
    },
    [reset, settle],
  );

  // System gesture, tab switch, incoming call, etc. — abandon the drag and snap
  // back to the current detent so the sheet never gets stuck mid-resize.
  const onPointerCancel = React.useCallback(() => {
    if (!drag.current.active) return;
    reset();
    const p = panel();
    if (p) snapTo(p, expanded.current ? fullHeight() : collapsedH.current);
  }, [reset, snapTo]);

  return { handleRef, onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
